import React, { useMemo, useState, useRef, useEffect, useContext, useCallback } from 'react';
import { ImAddressBook } from "react-icons/im";
import SelectedAppUsersForGroup from "./SelectedAppUsersForGroup";
import styles from './Editor/ComposeNotification.module.css';
import common from "../Style/common.module.css";
import EditorStyle from "../Style/EditorStyle.module.css";
import AuthContext from '../../store/auth-context';
import { getSelectUsersForGroup } from '../../services/group.service';

interface RecipientData {
    previewedRecipientData: any[];
    total: number;
    memberCountMismatch?: boolean;
}

interface PrivateRecipientDetails {
    firstName?: string;
    lastName?: string;
    title?: string;
    email?: string;
}

interface NotificationThreadHeaderProps {
    selectedNotificationThreadUuid: string | null;
    selectedGroupTitle: string;
    channels: string[];
    recipientData: RecipientData;
    recipientModalHendler: () => void;
    isCreatingRecipient?: boolean;
    privateRecipientDetails?: PrivateRecipientDetails | null;
}

const NotificationThreadHeader: React.FC<NotificationThreadHeaderProps> = ({
    selectedNotificationThreadUuid,
    selectedGroupTitle,
    channels,
    recipientData,
    recipientModalHendler,
    isCreatingRecipient = false,
    privateRecipientDetails = null
}) => {
    const authCtx = useContext(AuthContext);
    const [showSelectedUsersForGroup, setShowSelectedUsersForGroup] = useState(false);
    const [modalRecipients, setModalRecipients] = useState<any[]>(recipientData.previewedRecipientData || []);
    const [modalRecipientsTotal, setModalRecipientsTotal] = useState<number>(recipientData.total || 0);
    const [isFetchingRecipients, setIsFetchingRecipients] = useState(false);
    const RecipientsFocus = useRef<HTMLButtonElement>(null);

    const openRecipientsModal = useCallback(async () => {
        setShowSelectedUsersForGroup(true);
        if (!selectedNotificationThreadUuid) return;
        setIsFetchingRecipients(true);
        try {
            const data = await getSelectUsersForGroup(selectedNotificationThreadUuid, authCtx.token);
            if (data?.previewedRecipientData) {
                const parsed = typeof data.previewedRecipientData === 'string'
                    ? JSON.parse(data.previewedRecipientData)
                    : data.previewedRecipientData;
                setModalRecipients(Array.isArray(parsed) ? parsed : []);
                setModalRecipientsTotal(data.total ?? parsed.length);
            }
        } catch (err) {
            console.error('Failed to fetch recipients for modal:', err);
        } finally {
            setIsFetchingRecipients(false);
        }
    }, [selectedNotificationThreadUuid, authCtx.token]);

    useEffect(() => {
        if (RecipientsFocus.current && !selectedNotificationThreadUuid) {
            RecipientsFocus.current.focus();
        }
        // Reset fetched modal data whenever the thread changes
        setModalRecipients([]);
        setModalRecipientsTotal(0);
    }, [selectedNotificationThreadUuid]);
    
    // Determine if this is a private message (channel 5 with exactly 1 recipient)
    const isPrivateMessage = useMemo(() => {
        const recipients = recipientData.previewedRecipientData || [];
        return channels.includes("5") && recipients.length === 1;
    }, [channels, recipientData.previewedRecipientData]);

    // Get the recipient name for private messages
    const recipientDisplayName = useMemo(() => {
        if (!isPrivateMessage || !privateRecipientDetails) return '';
        
        return `${privateRecipientDetails.title || ''} ${privateRecipientDetails.firstName || ''} ${privateRecipientDetails.lastName || ''}`.trim();
    }, [isPrivateMessage, privateRecipientDetails]);

    return (
        <>
            {/* Recipient selection button - shown when creating new thread */}
            {!selectedNotificationThreadUuid && channels.length > 0 && (
                <div className={common.editorInner + ' ' + common.secondBlock}>
                    <div style={{ padding: '9px 17px 20px' }}>
                        {isCreatingRecipient ? (
                            <button 
                                ref={RecipientsFocus} 
                                style={{ color: '#FFFFFF' }} 
                                className={styles.Selectrecipientbtn} 
                                onClick={recipientModalHendler}
                            >
                                {privateRecipientDetails && privateRecipientDetails.firstName ? (
                                    <span>
                                        Private message to{' '}
                                        <strong>
                                            {privateRecipientDetails.title} {privateRecipientDetails.firstName} {privateRecipientDetails.lastName}
                                        </strong>
                                    </span>
                                ) : (
                                    <span>
                                        Recipients selected {recipientData.total > 0 ? `(${recipientData.total})` : ''}
                                    </span>
                                )}
                                <span className={EditorStyle.recipientIcon}>
                                    <i title={'Edit selection'} className="icon icon-pencil5"></i>
                                </span>
                            </button>
                        ) : (
                            <button 
                                ref={RecipientsFocus} 
                                className={styles.Selectrecipientbtn} 
                                onClick={recipientModalHendler}
                            >
                                {channels.includes("5") 
                                    ? "Select the recipient for private message." 
                                    : "Add recipients for this notification group"
                                }{" "}
                                <span className={EditorStyle.recipientIcon}>
                                    <i className="icon icon-plus-circle"></i>
                                </span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Existing thread header - shown when thread is selected */}
            {selectedNotificationThreadUuid && (
                <div className={`${styles.ComposeNotificationHeader} container ${common.firstBlock}`}>
                    <div className="row" style={{ height: "100%" }}>
                        <div className={`col-8 ${styles.SelectedUserGroupDetails}`}>
                            <strong className={common.threadHeaderTitle}>{selectedGroupTitle}</strong>
                            {isPrivateMessage && recipientDisplayName && (
                                <span className={styles.SelectedUserName}>
                                    Direct Messages to <strong>{recipientDisplayName}</strong>
                                    <span className="badge badge-primary" style={{ marginLeft: '8px', fontSize: '11px', padding: '4px 8px' }}>
                                        Private Message
                                    </span>
                                </span>
                            )}
                        </div>

                        {!isPrivateMessage && (
                            <div className="col-4 d-flex flex-row" style={{ alignItems: "center", justifyContent: "flex-end", paddingRight: "24px" }}>
                                {showSelectedUsersForGroup && (
                                    <SelectedAppUsersForGroup
                                        users={modalRecipientsTotal}
                                        recipients={isFetchingRecipients ? [] : modalRecipients}
                                        isLoading={isFetchingRecipients}
                                        show={showSelectedUsersForGroup}
                                        onHide={() => setShowSelectedUsersForGroup(false)}
                                    />
                                )}

                                <div className="btn-group btn-group-sm" role="group">
                                    <button
                                        onClick={openRecipientsModal}
                                        type="button"
                                        className="btn btn-secondary"
                                    >
                                        Recipients: {modalRecipientsTotal || recipientData.total}
                                    </button>

                                    <button
                                        className="btn btn-secondary"
                                        style={{ borderLeft: "1px solid #9f9f9f" }}
                                        type="button"
                                        onClick={recipientModalHendler}
                                    >
                                        <ImAddressBook style={{ height: "20px" }} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default NotificationThreadHeader;

